import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utcPlugin from 'dayjs/plugin/utc';

// ─────────────────────────────────────────────────────────────────────────────
// Dispatch-slot time conversion.
//
// A pitstop dispatch slot is a recurring wall-clock time plus the ISO weekdays
// it runs on, stored alongside the zone that wall-clock is written in. New
// slots are written in UTC so the row means the same instant to every caller;
// older rows still carry their original zone, so every read converts from the
// row's own zone rather than assuming one.
//
// Ops think in their own clock, so conversion happens at the edge: row zone →
// viewer's zone on the way in, viewer's zone → UTC on the way out. Crossing
// zones can move a slot across midnight, which moves its weekday with it —
// 22:30 UTC Monday is 05:30 Tuesday in Jakarta. That is why every helper
// reports a `dayShift`, and why the active-day set must be shifted by it.
// ─────────────────────────────────────────────────────────────────────────────

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

/** A wall-clock time paired with the day it lands on relative to the input. */
export interface ShiftedTime {
  /** `HH:mm` in the target zone. */
  time: string;
  /** -1, 0 or +1 — days the converted time moved relative to the source. */
  dayShift: number;
}

const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;
const YYYY_MM_DD = /^\d{4}-\d{2}-\d{2}$/;

/** `true` when `value` is a well-formed `HH:mm`. */
export const isHhMm = (value: string): boolean => HH_MM.test(value);

/** Whole days between two `YYYY-MM-DD` calendar dates. */
const daysBetween = (from: string, to: string): number =>
  dayjs(to).diff(dayjs(from), 'day');

/**
 * A slot's wall-clock in `zone` → the viewer's own clock.
 *
 * A recurring slot has no single date, but a zone's offset can move across the
 * year (DST). Anchoring on today keeps every conversion on one screen mutually
 * consistent and correct for the slots ops are looking at now.
 */
export const slotToLocal = (hhmm: string, zone: string): ShiftedTime => {
  if (!HH_MM.test(hhmm)) return { time: hhmm, dayShift: 0 };
  const tz = zone || 'UTC';
  const onDate = dayjs().tz(tz).format('YYYY-MM-DD');
  const local = dayjs.tz(`${onDate}T${hhmm}:00`, tz).local();
  return {
    time: local.format('HH:mm'),
    dayShift: daysBetween(onDate, local.format('YYYY-MM-DD')),
  };
};

/** The viewer's `HH:mm` → the UTC wall-clock to store. */
export const localSlotToUtc = (hhmm: string): ShiftedTime => {
  if (!HH_MM.test(hhmm)) return { time: hhmm, dayShift: 0 };
  const onDate = dayjs().format('YYYY-MM-DD');
  const asUtc = dayjs(`${onDate}T${hhmm}:00`).utc();
  return {
    time: asUtc.format('HH:mm'),
    dayShift: daysBetween(onDate, asUtc.format('YYYY-MM-DD')),
  };
};

/** Move a set of ISO weekdays (1 = Senin … 7 = Minggu) by `shift` days. */
export const shiftActiveDays = (days: number[], shift: number): number[] => {
  const moved =
    shift === 0 ? [...days] : days.map((d) => ((d - 1 + shift) % 7 + 7) % 7 + 1);
  return moved.sort((a, b) => a - b);
};

/**
 * Viewer's `YYYY-MM-DD` + `HH:mm` → the same instant's calendar date and
 * wall-clock in `zone`.
 *
 * The API identifies a slot by the wall-clock it was stored with, and reads
 * `dispatchDate` in that same zone — so an upload has to be expressed in the
 * schedule's zone, not the viewer's, even though the viewer picked it on their
 * own clock.
 */
export const localDateTimeInZone = (
  yyyyMmDd: string,
  hhmm: string,
  zone: string
): { date: string; time: string } | null => {
  if (!YYYY_MM_DD.test(yyyyMmDd) || !HH_MM.test(hhmm)) return null;
  const at = dayjs(`${yyyyMmDd}T${hhmm}:00`);
  if (!at.isValid()) return null;
  const inZone = at.tz(zone || 'UTC');
  return { date: inZone.format('YYYY-MM-DD'), time: inZone.format('HH:mm') };
};

/** ISO weekday (1 = Senin … 7 = Minggu) of a plain `YYYY-MM-DD`. */
export const isoWeekdayOf = (yyyyMmDd: string): number | null => {
  if (!YYYY_MM_DD.test(yyyyMmDd)) return null;
  const at = dayjs.utc(yyyyMmDd);
  if (!at.isValid()) return null;
  const day = at.day();
  return day === 0 ? 7 : day;
};
